<template>
  <div class="assignment-list-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="instructorName"
        current-portal="instructor"
        :disable-portal-link="!isVivaAdmin"
      />
    </div>

    <!-- Course Info Section -->
    <div class="instructor-info">
      <div class="instructor-row">
        <h2>Instructor{{ course.instructors && course.instructors.length > 1 ? 's' : '' }}:</h2>
        <div class="instructor-list">
          <span
            v-for="(inst, idx) in (course.instructors || [])"
            :key="idx"
            class="instructor-tag"
          >{{ formatName(inst.name || inst.email) }}</span>
        </div>
      </div>
      <div class="course-title-row">
        <h1 class="viva-page-title viva-page-title-small">Course: {{ course.title }}</h1>
        <button
          class="card-edit-btn card-edit-btn-orange course-edit-btn-small"
          @click="editCourse"
        >
          <span class="glyphicon glyphicon-pencil card-edit-icon"></span>
          EDIT
        </button>
      </div>
      <h1 class="viva-page-title viva-page-title-orange">ASSIGNMENTS</h1>
    </div>

    <!-- Assignment Cards Grid -->
    <div class="viva-card-grid">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <i class="glyphicon glyphicon-refresh spinning"></i>
        <h3>Loading assignments...</h3>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="empty-state">
        <i class="glyphicon glyphicon-exclamation-sign"></i>
        <h3>Error</h3>
        <p>{{ error }}</p>
        <button class="viva-btn viva-btn-orange" @click="loadCourseAndAssignments">Try Again</button>
      </div>

      <!-- Assignment cards -->
      <template v-else>
        <div
          v-for="assignment in assignments"
          :key="assignment.id"
          class="viva-card viva-card-orange"
          @click="viewAssignment(assignment.id)"
        >
          <div class="card-title">{{ assignment.title }}</div>
          <div class="card-stats" v-if="assignment.is_sent">
            SENT: {{ formatDate(assignment.send_date) }}
          </div>
          <div class="card-stats" v-else>STATUS: DRAFT</div>
          <div class="card-date">DUE: {{ formatDate(assignment.due_date) }}</div>
          <div class="card-button">
            <button
              class="card-edit-btn card-edit-btn-orange"
              @click.stop="editAssignment(assignment.id)"
            >
              <span class="glyphicon glyphicon-pencil card-edit-icon"></span>
              EDIT
            </button>
          </div>
        </div>

        <!-- Create New Assignment Card -->
        <div
          class="viva-card viva-card-orange viva-card-create"
          @click="createNewAssignment"
        >
          <div class="card-create-content">
            <span class="create-icon">+</span>
            <div class="card-title">CREATE NEW ASSIGNMENT</div>
          </div>
        </div>
      </template>
    </div>

    <!-- Back Link -->
    <div class="back-section">
      <router-link to="/instructor/dashboard" class="back-link">
        <span>←</span> Back to MY COURSES
      </router-link>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  name: 'AssignmentList',
  components: { ProfileDropdown },

  data() {
    return {
      course: {},
      assignments: [],
      loading: true,
      error: null,
      instructorName: '',
      isVivaAdmin: false,
    };
  },

  mounted() {
    const statusData = getStatusData();
    if (statusData) {
      this.instructorName = statusData.student_name || '';
      this.isVivaAdmin = statusData.is_viva_admin || false;
    }
    this.loadCourseAndAssignments();
  },

  methods: {
    formatName(name) {
      if (name.includes(', ')) {
        const [last, first] = name.split(', ');
        return `${first} ${last}`;
      }
      return name;
    },

    async loadCourseAndAssignments() {
      this.loading = true;
      this.error = null;
      const courseId = this.$route.params.id;

      try {
        // Load course details
        const courseResponse = await apiClient.get(`/api/viva/instructor/course/${courseId}/`);
        this.course = courseResponse.data || {};

        // Load assignments for this course
        const assignmentsResponse = await apiClient.get(`/api/viva/instructor/course/${courseId}/assignments/`);
        this.assignments = assignmentsResponse.data.assignments || [];

      } catch (error) {
        console.error('Failed to load course/assignments:', error);
        this.error = handleApiError(error, this.$router, 'Failed to load assignments. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    createNewAssignment() {
      const courseId = this.$route.params.id;
      this.$router.push({
        name: 'assignment-edit',
        params: { id: 'new' },
        query: { course_id: courseId }
      });
    },

    editAssignment(assignmentId) {
      this.$router.push({
        name: 'assignment-edit',
        params: { id: assignmentId }
      });
    },

    viewAssignment(assignmentId) {
      this.$router.push({
        name: 'assignment-submissions',
        params: { id: assignmentId }
      });
    },

    editCourse() {
      const courseId = this.$route.params.id;
      this.$router.push({
        name: 'course-edit',
        params: { id: courseId },
        query: { return_to: 'assignments' }
      });
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.assignment-list-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 100px;
}

.instructor-info {
  margin-bottom: 0;
  padding-bottom: 10px;
}

.course-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.course-edit-btn-small {
  font-size: 12px;
  padding: 5px 10px;
  margin-bottom: 10px;
  background-color: var(--viva-green);
}

.course-edit-btn-small .card-edit-icon {
  width: 12px;
  height: 12px;
}

.viva-card-grid {
  padding-top: 10px;
  margin-left: 30px;
  margin-right: 30px;
  padding-left: 30px;
  padding-right: 30px;
}

.viva-page-title-orange {
  color: var(--viva-orange);
}

.viva-page-title-small {
  font-size: 24px;
  margin-top: 0;
}

.instructor-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.instructor-row h2 {
  margin: 0;
  white-space: nowrap;
}

.instructor-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.instructor-tag {
  font-size: 13px;
  font-weight: 600;
  color: var(--viva-dark-green, #333);
  background-color: rgba(0, 0, 0, 0.06);
  padding: 3px 10px;
  border-radius: 12px;
}
</style>
